import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PageBackButton from "../../common/PageBackButton";
import { useModalStyles } from "../../common/useModalStyles";
import ActivityTimeline, { ActivityEntry, ActivityTypeConfig } from "../../common/ActivityTimeline";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchOrderById, getOrderItemsByOrderId } from "../../../redux/slice/order/OrderSlice";
import { fetchInvoiceAgency, fetchInvoiceList, getInvoiceById } from "../../../redux/slice/invoice/InvoiceSlice";
import { fetchAuditLogList } from "../../../redux/slice/auditLog/auditLogSlice";
import { getPaymentsByInvoice } from "../../../redux/slice/payment/paymentSlice";
import { getV3CommissionTypeDisplayLabel, priceFormatter } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import {
  ActivityTabKey,
  asEntityNumber,
  buildActivityCenterPath,
  collectRelatedInvoices,
  displayEntityValue,
  extractAuditLogRows,
  extractInvoiceListRows,
  buildActivityTabEntries,
  parseActivityCenterSearch,
  toAmountNumber,
  toPositiveId,
  getCommissionBreakdown,
  uniqueAuditLogRows,
} from "../../../utils/activityCenterUtils";

const AUDIT_PAGE_SIZE = 100;

const ACTION_TYPE_STYLE: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  CREATE: { label: "Created", color: "#14b8a6", icon: <AddCircleOutlineIcon /> },
  ADD: { label: "Added", color: "#22c55e", icon: <AddCircleOutlineIcon /> },
  MODIFY: { label: "Modified", color: "#3b82f6", icon: <EditOutlinedIcon /> },
  UPDATE: { label: "Updated", color: "#6366f1", icon: <EditOutlinedIcon /> },
  DELETE: { label: "Deleted", color: "#dc3545", icon: <RemoveCircleOutlineIcon /> },
  CANCEL: { label: "Cancelled", color: "#dc3545", icon: <CancelOutlinedIcon /> },
  ACCESS: { label: "Access", color: "#64748b", icon: <VisibilityOutlinedIcon /> },
  VIEW: { label: "Viewed", color: "#64748b", icon: <VisibilityOutlinedIcon /> },
  EXPORT: { label: "Exported", color: "#0d9488", icon: <SendOutlinedIcon /> },
  STATUS: { label: "Status Change", color: "#3b82f6", icon: <SyncAltOutlinedIcon /> },
  WEBHOOK: { label: "Webhook", color: "#8b5cf6", icon: <TuneOutlinedIcon /> },
  OTHER: { label: "Activity", color: "#00B8D9", icon: <TuneOutlinedIcon /> },
};

const TAB_META: Record<ActivityTabKey, { label: string; subtitle: string; icon: React.ReactNode; accent: string }> = {
  all: {
    label: "All Activity",
    subtitle: "Every order, invoice, payment, and service fee event, in one chronological feed.",
    icon: <ListAltOutlinedIcon />,
    accent: "#00B8D9",
  },
  order: {
    label: "Order Activity",
    subtitle: "Chronological history of every change made to this order.",
    icon: <AssignmentOutlinedIcon />,
    accent: "#14b8a6",
  },
  invoice: {
    label: "Invoice Activity",
    subtitle: "Chronological history of every change made to this invoice.",
    icon: <ReceiptLongOutlinedIcon />,
    accent: "#0d9488",
  },
  payment: {
    label: "Payment Activity",
    subtitle: "Chronological history of every payment event on this invoice.",
    icon: <AccountBalanceWalletOutlinedIcon />,
    accent: "#22c55e",
  },
  commission: {
    label: "Service Fee Activity",
    subtitle: "Chronological history of every service fee event on this invoice.",
    icon: <CalculateOutlinedIcon />,
    accent: "#6366f1",
  },
};

const TAB_KEYS: ActivityTabKey[] = ["all", "order", "invoice", "payment", "commission"];

const payloadData = (result: any) => result?.payload?.data;

const buildTypeConfig = (entries: ActivityEntry[]): Record<string, ActivityTypeConfig> => {
  const config: Record<string, ActivityTypeConfig> = {};
  entries.forEach((entry) => {
    if (config[entry.type]) return;
    const known = ACTION_TYPE_STYLE[entry.type] || ACTION_TYPE_STYLE.OTHER;
    config[entry.type] = {
      label: ACTION_TYPE_STYLE[entry.type]?.label || entry.type.replace(/[_-]+/g, " "),
      color: known.color,
      icon: known.icon,
    };
  });
  return config;
};

const toTimelineEntries = (entries: ReturnType<typeof buildActivityTabEntries>): ActivityEntry[] =>
  entries.map(({ relatedTab: _relatedTab, ...entry }) => entry);

const ActivityCenter: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { tokens } = useModalStyles();
  const dispatch = useAppDispatch();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const v3EntityLabel = useMemo(
    () => getV3CommissionTypeDisplayLabel(allCodeTablesValuesMap),
    [allCodeTablesValuesMap]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseActivityCenterSearch(searchParams.toString()), [searchParams]);
  const [activeTab, setActiveTab] = useState<ActivityTabKey>((query.tab as ActivityTabKey) || "all");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [invoice, setInvoice] = useState<any>(null);
  const [relatedInvoices, setRelatedInvoices] = useState<{ invoiceId?: number; invoiceNumber?: string }[]>([]);
  const [invoiceAgencies, setInvoiceAgencies] = useState<any[]>([]);
  const [invoicePayments, setInvoicePayments] = useState<any[]>([]);
  const [orderAuditRows, setOrderAuditRows] = useState<any[]>([]);
  const [invoiceAuditRows, setInvoiceAuditRows] = useState<any[]>([]);

  useEffect(() => {
    if (query.tab) {
      setActiveTab(query.tab as ActivityTabKey);
    }
  }, [query.tab]);

  useEffect(() => {
    let cancelled = false;

    const fetchAuditPages = async (entityName: string, entityNumber: string) => {
      const first = await dispatch(
        fetchAuditLogList({
          entityName,
          entityNumber,
          currentPage: 1,
          itemsPerPage: AUDIT_PAGE_SIZE,
          userName: null,
          fromDate: undefined,
          toDate: undefined,
        }) as any
      );
      const firstPage = first?.payload?.data?.searchAuditData;
      const rows = extractAuditLogRows(first?.payload);
      const totalPages = Number(firstPage?.totalPages) || 1;
      if (totalPages <= 1) return rows;
      const remaining: any[] = [];
      for (let page = 2; page <= totalPages; page++) {
        const next = await dispatch(
          fetchAuditLogList({
            entityName,
            entityNumber,
            currentPage: page,
            itemsPerPage: AUDIT_PAGE_SIZE,
            userName: null,
            fromDate: undefined,
            toDate: undefined,
          }) as any
        );
        remaining.push(...extractAuditLogRows(next?.payload));
      }
      return [...rows, ...remaining];
    };

    const load = async () => {
      const hasContext = Boolean(query.orderId || query.invoiceId || query.orderNumber || query.invoiceNumber);
      if (!hasContext) {
        setOrder(null);
        setInvoice(null);
        setRelatedInvoices([]);
        setInvoiceAgencies([]);
        setInvoicePayments([]);
        setOrderAuditRows([]);
        setInvoiceAuditRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let nextOrder: any = null;
        let nextInvoice: any = null;

        if (query.invoiceId) {
          nextInvoice = payloadData(
            await dispatch(getInvoiceById({ invoiceId: query.invoiceId, isSaveAuditLog: false, skipLoader: true }) as any)
          );
        }
        if (query.orderId) {
          nextOrder = payloadData(
            await dispatch(fetchOrderById({ orderId: query.orderId, isSaveAuditLog: false }) as any)
          );
        }

        const relatedInvoiceId = query.invoiceId || toPositiveId(nextOrder?.invoiceId);
        const relatedOrderId = query.orderId || toPositiveId(nextInvoice?.orderId);

        if (!nextInvoice && relatedInvoiceId) {
          nextInvoice = payloadData(
            await dispatch(getInvoiceById({ invoiceId: relatedInvoiceId, isSaveAuditLog: false, skipLoader: true }) as any)
          );
        }
        if (!nextOrder && relatedOrderId) {
          nextOrder = payloadData(
            await dispatch(fetchOrderById({ orderId: relatedOrderId, isSaveAuditLog: false }) as any)
          );
        }

        let orderItems: any[] = Array.isArray(nextOrder?.orderItems) ? nextOrder.orderItems : [];
        const orderIdForItems = relatedOrderId || toPositiveId(nextOrder?.orderId);
        if (orderIdForItems && orderItems.length === 0) {
          const itemPayload = payloadData(await dispatch(getOrderItemsByOrderId(orderIdForItems) as any));
          orderItems = Array.isArray(itemPayload) ? itemPayload : extractInvoiceListRows({ data: itemPayload });
          if (orderItems.length > 0) {
            nextOrder = { ...(nextOrder || {}), orderItems };
          }
        }

        const orderNumber =
          query.orderNumber || asEntityNumber(nextOrder?.orderNumber) || asEntityNumber(nextInvoice?.orderNumber);
        let extraInvoiceRows: any[] = [...orderItems];
        let related = collectRelatedInvoices(query, nextOrder, nextInvoice, extraInvoiceRows);

        if (related.length === 0 && orderNumber) {
          const listResult = await dispatch(
            fetchInvoiceList({
              fromDate: undefined,
              toDate: undefined,
              agencyIds: [],
              patientIds: [],
              mfgIds: [],
              practiceIds: [],
              invoiceStatus: [],
              currentPage: 1,
              itemsPerPage: 100,
              isDownload: false,
              excludePurchaseAndBill: false,
              orderNumber,
            } as any)
          );
          extraInvoiceRows = [...extraInvoiceRows, ...extractInvoiceListRows(listResult?.payload)];
          related = collectRelatedInvoices(query, nextOrder, nextInvoice, extraInvoiceRows);
        }

        const invoicesNeedingDetail = related.filter((item) => item.invoiceId && !item.invoiceNumber);
        if (invoicesNeedingDetail.length > 0) {
          const fetchedDetails = (
            await Promise.all(
              invoicesNeedingDetail.slice(0, 20).map((item) =>
                dispatch(
                  getInvoiceById({ invoiceId: item.invoiceId as number, isSaveAuditLog: false, skipLoader: true }) as any
                )
              )
            )
          )
            .map((result) => payloadData(result))
            .filter(Boolean);
          extraInvoiceRows = [...extraInvoiceRows, ...fetchedDetails];
          related = collectRelatedInvoices(query, nextOrder, nextInvoice, extraInvoiceRows);
          if (!nextInvoice && fetchedDetails.length > 0) nextInvoice = fetchedDetails[0];
        }

        if (!nextInvoice) {
          const firstInvoiceId = related.find((item) => item.invoiceId)?.invoiceId;
          if (firstInvoiceId) {
            nextInvoice = payloadData(
              await dispatch(
                getInvoiceById({ invoiceId: firstInvoiceId, isSaveAuditLog: false, skipLoader: true }) as any
              )
            );
            related = collectRelatedInvoices(query, nextOrder, nextInvoice, extraInvoiceRows);
          }
        }

        const commissionInvoiceId =
          query.invoiceId || related.find((item) => item.invoiceId)?.invoiceId || toPositiveId(nextInvoice?.invoiceId);
        let nextAgencies: any[] = [];
        let nextPayments: any[] = [];
        if (commissionInvoiceId) {
          const [agencyResult, paymentResult] = await Promise.all([
            dispatch(fetchInvoiceAgency(commissionInvoiceId) as any),
            dispatch(getPaymentsByInvoice(commissionInvoiceId) as any),
          ]);
          const agencyData = payloadData(agencyResult);
          nextAgencies = Array.isArray(agencyData) ? agencyData : [];
          const paymentData = payloadData(paymentResult);
          nextPayments = Array.isArray(paymentData?.payments)
            ? paymentData.payments
            : Array.isArray(paymentData)
              ? paymentData
              : [];
        }

        const invoiceNumbers = Array.from(
          new Set(
            related
              .map((item) => item.invoiceNumber)
              .concat([
                query.invoiceNumber,
                asEntityNumber(nextInvoice?.mfgInvoiceNumber),
                asEntityNumber(nextInvoice?.invoiceNumber),
              ])
              .filter((value): value is string => Boolean(value))
          )
        );

        const [orderAuditRows, invoiceAuditGroups] = await Promise.all([
          orderNumber ? fetchAuditPages("ORDER", orderNumber) : Promise.resolve([]),
          Promise.all(invoiceNumbers.map((invoiceNumber) => fetchAuditPages("INVOICE", invoiceNumber))),
        ]);
        const invoiceAuditRows = uniqueAuditLogRows(invoiceAuditGroups.flat());

        if (cancelled) return;
        setOrder(nextOrder);
        setInvoice(nextInvoice);
        setRelatedInvoices(related);
        setInvoiceAgencies(nextAgencies);
        setInvoicePayments(nextPayments);
        setOrderAuditRows(orderAuditRows);
        setInvoiceAuditRows(invoiceAuditRows);
      } catch (_error) {
        if (cancelled) return;
        setRelatedInvoices([]);
        setInvoiceAgencies([]);
        setInvoicePayments([]);
        setOrderAuditRows([]);
        setInvoiceAuditRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, query.orderId, query.invoiceId, query.orderNumber, query.invoiceNumber]);

  const handleTabChange = (tab: ActivityTabKey) => {
    setActiveTab(tab);
    const path = buildActivityCenterPath({
      tab,
      orderId: query.orderId,
      orderNumber: query.orderNumber,
      invoiceId: query.invoiceId,
      invoiceNumber: query.invoiceNumber,
    });
    const qs = path.includes("?") ? path.split("?")[1] : "";
    setSearchParams(new URLSearchParams(qs), { replace: true });
  };

  const allAuditRows = useMemo(
    () => [...orderAuditRows, ...invoiceAuditRows],
    [orderAuditRows, invoiceAuditRows]
  );

  const tabEntries: Record<ActivityTabKey, ActivityEntry[]> = useMemo(
    () => ({
      all: toTimelineEntries(buildActivityTabEntries(allAuditRows, "all")),
      order: toTimelineEntries(buildActivityTabEntries(allAuditRows, "order")),
      invoice: toTimelineEntries(buildActivityTabEntries(allAuditRows, "invoice")),
      payment: toTimelineEntries(buildActivityTabEntries(allAuditRows, "payment")),
      commission: toTimelineEntries(buildActivityTabEntries(allAuditRows, "commission")),
    }),
    [allAuditRows]
  );

  const active = TAB_META[activeTab] || TAB_META.all;
  const activeEntries = tabEntries[activeTab] || [];
  const typeConfig = useMemo(() => buildTypeConfig(activeEntries), [activeEntries]);

  const practiceName = displayEntityValue(invoice?.practiceName || order?.practiceName);
  const manufacturerName = displayEntityValue(
    invoice?.mfgName || invoice?.manufacturerName || order?.manufacturerName
  );
  const productName = displayEntityValue(invoice?.productName || order?.productName);
  const orderNumberLabel = asEntityNumber(order?.orderNumber) || query.orderNumber;
  const invoiceNumberLabel =
    relatedInvoices
      .map((item) => item.invoiceNumber)
      .filter((value): value is string => Boolean(value))
      .filter((value, index, all) => all.indexOf(value) === index)
      .join(", ") ||
    asEntityNumber(invoice?.mfgInvoiceNumber) ||
    query.invoiceNumber;

  const paidAmount = toAmountNumber(invoice?.v3InvoicePaidAmount ?? invoice?.payment?.totalAmount);
  const outstandingAmount = toAmountNumber(invoice?.outstandingAmt ?? invoice?.outstandingAmount);
  const commissionBreakdown = getCommissionBreakdown(invoice, {
    invoiceAgencies,
    payments: invoicePayments,
    v3EntityLabel,
  });

  const entityFields = [
    { label: "Customer", value: practiceName },
    { label: "Manufacturer", value: manufacturerName },
    { label: "Product", value: productName },
  ];
  const amountFields = [
    { label: "Order Total", value: priceFormatter(toAmountNumber(order?.orderTotalPrice)), color: undefined as string | undefined },
    { label: "Invoice Total", value: priceFormatter(toAmountNumber(invoice?.invoiceAmount)) },
    { label: "Paid", value: priceFormatter(paidAmount) },
    { label: "Outstanding", value: priceFormatter(outstandingAmount), color: outstandingAmount > 0 ? "#dc3545" : undefined },
  ];

  const contextSubtitle = [orderNumberLabel && `Order ${orderNumberLabel}`, invoiceNumberLabel && `Invoice ${invoiceNumberLabel}`]
    .filter(Boolean)
    .join(" · ");

  const cardSx = {
    borderRadius: "14px",
    border: `1px solid ${isDark ? "rgba(148,163,184,0.2)" : "#e5e9ed"}`,
    backgroundColor: isDark ? "#141720" : "#ffffff",
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 1px 2px rgba(15,23,42,0.04), 0 2px 8px rgba(15,23,42,0.06)",
  };

  const hasContext = Boolean(query.orderId || query.invoiceId || query.orderNumber || query.invoiceNumber);

  return (
    <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "40px" }}>
      <Box mb={1.5} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} />
        <Box>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: tokens.textPrimary, lineHeight: 1.1 }}>
            Activity Center
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: tokens.textSecondary, mt: 0.4 }}>
            {contextSubtitle || "Review order, invoice, payment, and commission activity in one place."}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ...cardSx,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: { xs: 2, md: 3 },
          px: { xs: 2, md: 2.5 },
          py: 1.75,
          mb: 2,
        }}
      >
        {entityFields.map((field, index) => (
          <React.Fragment key={field.label}>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: tokens.textPrimary,
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {field.value}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.2 }}>
                ({field.label})
              </Typography>
            </Box>
            {index < entityFields.length - 1 ? (
              <Box
                sx={{ width: "1px", height: 32, backgroundColor: isDark ? "rgba(148,163,184,0.2)" : "#e5e9ed", flexShrink: 0 }}
              />
            ) : null}
          </React.Fragment>
        ))}
        <Box
          sx={{ width: "1px", height: 32, backgroundColor: isDark ? "rgba(148,163,184,0.2)" : "#e5e9ed", flexShrink: 0 }}
        />
        {amountFields.map((field, index) => (
          <React.Fragment key={field.label}>
            <Box sx={{ flex: 1, minWidth: 100 }}>
              <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, lineHeight: 1.2 }}>
                {field.label}
              </Typography>
              <Typography
                sx={{ fontSize: "0.9rem", fontWeight: 600, color: field.color || tokens.textPrimary, mt: 0.2 }}
              >
                {field.value}
              </Typography>
            </Box>
            {index < amountFields.length - 1 ? (
              <Box
                sx={{ width: "1px", height: 32, backgroundColor: isDark ? "rgba(148,163,184,0.2)" : "#e5e9ed", flexShrink: 0 }}
              />
            ) : null}
          </React.Fragment>
        ))}
        {commissionBreakdown.items.length > 0 ? (
          <>
            <Box
              sx={{ width: "1px", height: 32, backgroundColor: isDark ? "rgba(148,163,184,0.2)" : "#e5e9ed", flexShrink: 0 }}
            />
            <Box sx={{ flex: 1.6, minWidth: 180 }} data-testid="commission-header">
              <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, lineHeight: 1.2 }}>
                Commission
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", fontWeight: 600, mt: 0.25, lineHeight: 1.4 }}
                data-testid="commission-breakdown"
              >
                {commissionBreakdown.items.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 ? " · " : null}
                    <Box component="span" data-testid={`commission-${item.key}`}>
                      {item.label}{" "}
                      <Box
                        component="span"
                        data-testid={`commission-${item.key}-amount`}
                        sx={{ color: item.paid ? "#22c55e" : "#dc3545" }}
                      >
                        {priceFormatter(item.amount)}
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </Typography>
            </Box>
          </>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {TAB_KEYS.map((key) => {
          const tab = TAB_META[key];
          const selected = key === activeTab;
          return (
            <Box
              key={key}
              data-testid={`activity-tab-${key}`}
              onClick={() => handleTabChange(key)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.9,
                px: 1.8,
                py: 1,
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.86rem",
                border: `1.5px solid ${selected ? tab.accent : isDark ? "rgba(148,163,184,0.24)" : "rgba(15,23,42,0.12)"}`,
                backgroundColor: selected ? `${tab.accent}1f` : isDark ? "rgba(148,163,184,0.05)" : "#ffffff",
                color: selected ? tab.accent : tokens.textSecondary,
                transition: "all 0.15s ease",
                "&:hover": { borderColor: tab.accent, color: tab.accent },
                "& svg": { fontSize: "1.1rem" },
              }}
            >
              {tab.icon}
              {tab.label}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ ...cardSx, p: { xs: "16px", md: "22px" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${active.accent}1f`,
              border: `1px solid ${active.accent}55`,
              color: active.accent,
              flexShrink: 0,
            }}
          >
            {active.icon}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: tokens.textPrimary, lineHeight: 1.2 }}>
              {active.label}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }}>{active.subtitle}</Typography>
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }} data-testid="activity-loading">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <ActivityTimeline
            key={activeTab}
            entries={activeEntries}
            typeConfig={typeConfig}
            emptyText={
              hasContext
                ? "No activity yet."
                : "Open Activity Center from an order or invoice to see its history."
            }
          />
        )}
      </Box>
    </Box>
  );
};

export default ActivityCenter;
